<template>
  <v-dialog :modelValue="modelValue" max-width="588" persistent @update:modelValue="$emit('update:modelValue', $event)">
    <div class="account-match" data-testid="existing-account-match-modal">
      <div class="account-match__body">
        <div class="account-match__title-row">
          <h2 class="account-match__title">{{ heading }}</h2>
          <button
            v-if="step !== 'linked'"
            type="button"
            class="account-match__close"
            aria-label="Close"
            :disabled="busy"
            data-testid="account-match-close"
            @click="$emit('skip')"
          >
            <v-icon size="24" color="#191919">mdi-close</v-icon>
          </button>
        </div>

        <!-- Step 1: we found an account, is it yours? -->
        <template v-if="step === 'confirm'">
          <p class="account-match__text">
            {{
              matches.length === 1
                ? 'It looks like you may already have an account. To keep your courses and records in one place, we’ll verify the email address on that account before continuing.'
                : 'It looks like you may already have an account. Select the email address you have access to and we’ll verify it, so your courses and records stay in one place.'
            }}
          </p>

          <p v-if="matches.length === 1" class="account-match__email" data-testid="account-match-email">
            {{ matches[0].maskedEmail }}
          </p>

          <!-- More than one contact matched the name + birthday; let the learner say which mailbox is theirs. -->
          <v-radio-group v-else v-model="selectedRef" hide-details density="comfortable" class="account-match__choices">
            <v-radio
              v-for="match in matches"
              :key="match.candidateRef"
              :value="match.candidateRef"
              color="#8c1d40"
              :data-testid="`account-match-option-${match.candidateRef}`"
            >
              <template v-slot:label>
                <span class="account-match__email">{{ match.maskedEmail }}</span>
              </template>
            </v-radio>
          </v-radio-group>

          <p class="account-match__text">We'll send a verification code to confirm it's you.</p>
        </template>

        <!-- Step 2: enter the code -->
        <template v-else-if="step === 'code'">
          <p class="account-match__text">
            We sent a verification code to <strong>{{ maskedEmail }}</strong
            >. Enter it below to link your ASURITE to that account.
          </p>

          <v-text-field
            v-model="otpCode"
            label="Verification code"
            variant="outlined"
            inputmode="numeric"
            autocomplete="one-time-code"
            maxlength="10"
            hide-details
            data-testid="account-match-code-input"
            @keyup.enter="verifyCode"
          />

          <p class="account-match__text account-match__text--small">
            Didn't get it?
            <a href="#" class="account-match__link" data-testid="account-match-resend" @click.prevent="sendCode"
              >Send a new code</a
            >
          </p>
        </template>

        <!-- Step 3: done -->
        <p v-else-if="step === 'linked'" class="account-match__text">
          Your ASURITE is now linked to the account for <strong>{{ maskedEmail }}</strong
          >. We'll take you to your dashboard.
        </p>

        <div v-if="errorMessage" class="account-match__error" data-testid="account-match-error">
          <v-icon size="20" class="mr-2" color="#cc2f2f">mdi-alert</v-icon>
          <span>{{ errorMessage }}</span>
        </div>
      </div>

      <div class="account-match__actions">
        <template v-if="step === 'linked'">
          <button
            type="button"
            class="account-match__btn account-match__btn--gold"
            data-testid="account-match-go-to-account"
            @click="$emit('linked')"
          >
            Go to my dashboard
          </button>
        </template>

        <template v-else>
          <button
            type="button"
            class="account-match__btn account-match__btn--outline"
            :disabled="busy"
            data-testid="account-match-skip"
            @click="$emit('skip')"
          >
            {{ step === 'code' ? 'Skip and keep registering' : 'This isn’t my email' }}
          </button>

          <button
            v-if="step === 'confirm'"
            type="button"
            class="account-match__btn account-match__btn--gold"
            :disabled="busy || !selectedRef"
            data-testid="account-match-send-code"
            @click="sendCode"
          >
            <v-progress-circular v-if="busy" indeterminate size="18" width="2" class="mr-2" />
            Send verification code
          </button>

          <button
            v-else
            type="button"
            class="account-match__btn account-match__btn--gold"
            :disabled="busy || !otpCode"
            data-testid="account-match-verify"
            @click="verifyCode"
          >
            <v-progress-circular v-if="busy" indeterminate size="18" width="2" class="mr-2" />
            Verify and link
          </button>
        </template>
      </div>
    </div>
  </v-dialog>
</template>

<script>
import { defineComponent } from 'vue';
import { Logger } from '../mixins/logger';

/**
 * ULC-3349: offers a learner arriving through Continue with ASURITE the chance to attach that
 * ASURITE to an account we already hold for them, instead of registering a duplicate. Soft by
 * design, every state can be walked away from, and the only thing that authorises a link is a
 * code sent to the address already on the existing account.
 */
export default defineComponent({
  name: 'ExistingAccountMatchModal',

  mixins: [Logger],

  props: {
    modelValue: {
      type: Boolean,
      default: false
    },
    matches: {
      type: Array,
      default: () => []
    },
    lastName: {
      type: String,
      default: ''
    },
    birthDate: {
      type: String,
      default: ''
    },
    asuRite: {
      type: String,
      default: ''
    }
  },

  emits: ['update:modelValue', 'skip', 'linked'],

  data() {
    return {
      step: 'confirm',
      selectedRef: '',
      otpCode: '',
      otpId: '',
      maskedEmail: '',
      errorMessage: '',
      busy: false
    };
  },

  computed: {
    heading() {
      if (this.step === 'linked') return 'Account linked';

      return this.step === 'code' ? 'Check your email' : 'We found an existing account';
    }
  },

  watch: {
    // The form can surface a different match set if the learner edits their name or birthday,
    // so every open starts from the first step.
    modelValue(open) {
      if (open) this.reset();
    },

    matches: {
      immediate: true,
      handler(matches) {
        // Pre-select when there is nothing to choose between.
        this.selectedRef = matches.length === 1 ? matches[0].candidateRef : '';
      }
    },

    // A failure against one address says nothing about the next one they pick.
    selectedRef() {
      this.errorMessage = '';
    }
  },

  methods: {
    reset() {
      this.step = 'confirm';
      this.otpCode = '';
      this.otpId = '';
      this.maskedEmail = '';
      this.errorMessage = '';
      this.busy = false;
      this.selectedRef = this.matches.length === 1 ? this.matches[0].candidateRef : '';
    },

    async sendCode() {
      if (!this.selectedRef) return;

      this.busy = true;
      this.errorMessage = '';

      try {
        const result = await this.$conn.sendExistingAccountCode({
          lastName: this.lastName,
          birthDate: this.birthDate,
          candidateRef: this.selectedRef
        });

        this.otpId = result.otpId;
        this.maskedEmail = result.maskedEmail;
        this.otpCode = '';
        this.step = 'code';
      } catch (error) {
        this.logError('ExistingAccountMatchModal sendCode', error);

        this.errorMessage = error.message;
      } finally {
        this.busy = false;
      }
    },

    async verifyCode() {
      if (!this.otpCode) return;

      this.busy = true;
      this.errorMessage = '';

      try {
        await this.$conn.linkAsuriteToExistingAccount({
          lastName: this.lastName,
          birthDate: this.birthDate,
          candidateRef: this.selectedRef,
          asuRite: this.asuRite,
          otpId: this.otpId,
          otpCode: this.otpCode.trim()
        });

        this.step = 'linked';
      } catch (error) {
        this.logError('ExistingAccountMatchModal verifyCode', error);

        this.errorMessage = error.message;
      } finally {
        this.busy = false;
      }
    }
  }
});
</script>

<style lang="stylus" scoped>
// Values mirror the Figma "Modal" frame (ULC-3349 section, node 39:486).
.account-match {
  display: flex;
  flex-direction: column;
  gap: 24px;
  padding: 24px;
  background: #ffffff;
  border-radius: 12px;
  font-family: Arial, 'Roboto', sans-serif;
  color: #191919;
}

.account-match__body {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.account-match__title-row {
  display: flex;
  align-items: center;
  gap: 16px;
}

.account-match__title {
  flex: 1 0 0;
  margin: 0;
  font-size: 24px;
  font-weight: 700;
  line-height: 28px;
  letter-spacing: -0.72px;
  overflow-wrap: break-word;
}

.account-match__close {
  display: inline-flex;
  padding: 0;
  border: 0;
  background: none;
  cursor: pointer;
}

.account-match__close:disabled {
  cursor: default;
  opacity: 0.4;
}

.account-match__text {
  margin: 0;
  font-size: 16px;
  line-height: 24px;
  letter-spacing: -0.48px;
  overflow-wrap: break-word;
}

.account-match__text--small {
  font-size: 14px;
  line-height: 20px;
  letter-spacing: 0;
}

.account-match__email {
  margin: 0;
  font-size: 16px;
  font-weight: 700;
  line-height: 24px;
  letter-spacing: -0.48px;
  color: #000000;
}

.account-match__choices {
  margin: 0;
}

.account-match__link {
  color: #8c1d40;
  text-decoration: underline;
}

.account-match__link:hover {
  text-decoration: none;
}

.account-match__error {
  display: flex;
  align-items: flex-start;
  background-color: #f7dddd;
  border: 1px solid #cc2f2f;
  border-radius: 4px;
  padding: 12px 16px;
  font-size: 14px;
  line-height: 20px;
}

.account-match__actions {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
}

.account-match__btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 40px;
  border-radius: 100px;
  font-family: inherit;
  font-size: 16px;
  font-weight: 700;
  line-height: 24px;
  white-space: nowrap;
  cursor: pointer;
  transition: background-color 0.2s ease-in-out;
}

.account-match__btn:disabled {
  cursor: not-allowed;
  opacity: 0.5;
}

.account-match__btn:focus-visible {
  outline: 2px solid #ffc627;
  outline-offset: 2px;
}

.account-match__btn--outline {
  padding: 8px 16px;
  border: 1px solid #000000;
  background: #ffffff;
  color: #000000;
}

.account-match__btn--outline:hover:not(:disabled) {
  background: rgba(0, 0, 0, 0.06);
}

.account-match__btn--gold {
  min-width: 112px;
  padding: 16px 32px;
  border: 0;
  background: #ffc627;
  color: #191919;
}

.account-match__btn--gold:hover:not(:disabled) {
  background: #e6b223;
}
</style>
